import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API_BASE_URL from '../apiConfig';

// Async thunk to fetch user's admin clubs
export const fetchUserPermissions = createAsyncThunk(
  'permissions/fetchUserPermissions',
  async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}/admin-clubs`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch user permissions');
      }
      
      const adminClubs = await response.json();
      
      // Extract club IDs and create a permissions map
      const permissions = {
        adminClubIds: adminClubs.map(id => String(id)), // Convert all IDs to strings
        adminClubs: [], // No longer storing full club data
        isLoaded: true
      };
      
      return permissions;
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      throw error;
    }
  }
);

const permissionsSlice = createSlice({
  name: 'permissions',
  initialState: {
    adminClubIds: [],
    adminClubs: [],
    isLoaded: false,
    loading: false,
    error: null
  },
  reducers: {
    clearPermissions: (state) => {
      state.adminClubIds = [];
      state.adminClubs = [];
      state.isLoaded = false;
      state.loading = false;
      state.error = null;
    },
    addAdminClub: (state, action) => {
      const clubId = action.payload;
      if (!state.adminClubIds.includes(clubId)) {
        state.adminClubIds.push(clubId);
      }
    },
    removeAdminClub: (state, action) => {
      const clubId = action.payload;
      state.adminClubIds = state.adminClubIds.filter(id => id !== clubId);
      state.adminClubs = state.adminClubs.filter(club => club.id !== clubId);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.adminClubIds = action.payload.adminClubIds;
        state.adminClubs = action.payload.adminClubs;
        state.isLoaded = true;
        state.error = null;
      })
      .addCase(fetchUserPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.isLoaded = false;
      });
  }
});

export const { clearPermissions, addAdminClub, removeAdminClub } = permissionsSlice.actions;

// Selectors
export const selectAdminClubIds = (state) => state.permissions.adminClubIds;
export const selectAdminClubs = (state) => state.permissions.adminClubs;
export const selectPermissionsLoaded = (state) => state.permissions.isLoaded;
export const selectPermissionsLoading = (state) => state.permissions.loading;
export const selectPermissionsError = (state) => state.permissions.error;

// Helper selector to check if user has permissions for a specific club
export const selectHasClubPermissions = (state, clubId) => {
  if (!clubId) return false;
  return state.permissions.adminClubIds.includes(String(clubId));
};

// Helper selector to check if user owns a specific club
// Note: Since we only have club IDs now, we can't distinguish between owner and admin
// This will need to be enhanced if we need to distinguish roles
export const selectIsClubOwner = (state, clubId) => {
  if (!clubId) return false;
  // For now, if user has permissions for a club, we'll assume they can edit it
  // This includes owners, admins, and mods
  return state.permissions.adminClubIds.includes(String(clubId));
};

export default permissionsSlice.reducer;
